import api from '../utils/api';
import { ApiResponse } from "../utils/api/ApiProps";

export interface PipelineDetail {
  pid: number;
  uuid: string;
  pipeline_name: string;
  pipeline_description?: string;
  created_at: string;
  updated_at: string;
  status_options?: StatusOption[];
  role_id_fk?: number | null;
  role_uuid?: string | null;
  assignedRole?: {
    role_id: number;
    uuid: string;
    role_name: string;
    role_level: number;
  };
}

export interface StatusOption {
  opt_id: number;
  uuid: string;
  opt_name: string;
  opt_value: string;
  opt_order: number;
  opt_active: boolean;
}

export interface PipelineFormData {
  pipeline_name: string;
  pipeline_description: string;
  status_options: string[]; // Array of status option UUIDs
}

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

interface PipelinesApiData {
  pipelines: PipelineDetail[];
  pagination: PaginationProps;
}

interface GetPipelineParams {
  page?: number;
  search_term?: string;
  sort_column?: string;
  sort_order?: 'ASC' | 'DESC';
}

export const getPipelines = async ({
  page = 1,
  search_term = "",
  sort_column,
  sort_order,
}: GetPipelineParams = {}): Promise<ApiResponse<PipelinesApiData>> => {
  try {
    const searchParams = new URLSearchParams();

    searchParams.append("page", page.toString());
    if (search_term) searchParams.append("search_term", search_term);
    if (sort_column) searchParams.append("sort_column", sort_column);
    if (sort_order) searchParams.append("sort_order", sort_order);

    const queryString = searchParams.toString();
    const url = queryString ? `/pipelines?${queryString}` : "/pipelines";

    return await api.get<PipelinesApiData>(url);
  } catch (error) {
    // Fallback to empty data if endpoint doesn't exist
    console.warn('Pipeline endpoint not available, returning empty data:', error);
    return {
      success: false,
      status: "error",
      message: "Failed to fetch pipelines",
      data: {
        pipelines: [],
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
          itemsPerPage: 10,
          hasNextPage: false,
          hasPreviousPage: false,
        }
      },
      totalPages: 1,
      total: 0
    } as ApiResponse<PipelinesApiData>;
  }
};

export const getPipelineById = async (uuid: string): Promise<ApiResponse<{ data: PipelineDetail }>> => {
  const response = await api.req<{ data: PipelineDetail }>({
    url: `/pipelines/${uuid}`,
    method: api.Method.GET,
  });
  return response;
};

export const createPipeline = async (data: PipelineFormData): Promise<ApiResponse<{ data: PipelineDetail }>> => {
  const response = await api.req<{ data: PipelineDetail }>({
    url: '/pipelines',
    method: api.Method.POST,
    body: data,
  });
  return response;
};

export const updatePipeline = async (uuid: string, data: PipelineFormData): Promise<ApiResponse<{ data: PipelineDetail }>> => {
  const response = await api.req<{ data: PipelineDetail }>({
    url: `/pipelines/${uuid}`,
    method: api.Method.PUT,
    body: data,
  });
  return response;
};

export const getStatusOptions = async (): Promise<ApiResponse<{ options: StatusOption[] }>> => {
  try {
    // Fetch all columns with options, then filter for status column
    const searchParams = new URLSearchParams();
    searchParams.append("include", "options");
    searchParams.append("all", "true");
    searchParams.append("limit", "100");
    
    const response = await api.get(`/columns/?${searchParams.toString()}`);
    const responseData = response.data as { columns?: any[] };

    if (responseData && responseData.columns) {
      // Find the status column (col_name="status")
      const statusColumn = responseData.columns.find((column: any) => 
        column.col_name === 'status'
      );
      
      if (statusColumn && statusColumn.options) {
        // Filter only active options and sort by opt_order ASC
        const activeOptions = statusColumn.options
          .filter((option: any) => option.opt_active === true)
          .sort((a: any, b: any) => a.opt_order - b.opt_order)
          .map((option: any) => ({
            opt_id: option.opt_id,
            uuid: option.uuid,
            opt_name: option.opt_name,
            opt_value: option.opt_value,
            opt_order: option.opt_order,
            opt_active: option.opt_active
          }));
          
        return {
          success: true,
          status: "success",
          message: "Status options retrieved",
          data: {
            options: activeOptions
          },
          totalPages: 1,
          total: activeOptions.length
        } as ApiResponse<{ options: StatusOption[] }>;
      }
    }
    
    // If no status column found, return empty options
    return {
      success: true,
      status: "success",
      message: "No status options found",
      data: {
        options: []
      },
      totalPages: 1,
      total: 0
    } as ApiResponse<{ options: StatusOption[] }>;
    
  } catch (error) {
    console.error('Error fetching status options from columns:', error);
    // Fallback to empty options instead of mock data
    return {
      success: false,
      status: "error",
      message: "Failed to fetch status options",
      data: {
        options: []
      },
      totalPages: 1,
      total: 0
    } as ApiResponse<{ options: StatusOption[] }>;
  }
};